import { MediaPack } from '@calm/evexia-web-sdk';
import createMediaApi from '@rhg/libs/api/create-media-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery } from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { useActiveCircle } from '../use-active-circle';
import { useCircle } from '../use-circle';

export const mediaPackQueryOptions = {
    queryKey: function (packClass: string, circleId: number) {
        return [queryKeys.packByPackClass, packClass, circleId];
    },
    queryFn: async function (
        getAccessTokenSilently: () => Promise<string>,
        packClass: string,
        circleId: number,
        circleAuthorizationToken: string,
        rHGApp: string
    ): Promise<MediaPack | undefined> {
        const mediaApi = createMediaApi(getAccessTokenSilently);

        const response = await mediaApi.getPackByClass({
            recipientId: circleId,
            _class: packClass,
            circleAuthorizationToken,
            xDevicePlatform: 'www',
            rHGApp,
        });

        return response.data ?? undefined;
    },
    queryOptions: function (
        isAuthenticated: boolean,
        circleId: number,
        circleAuthorizationToken: string | undefined,
        packClass?: string
    ) {
        return {
            enabled: !!(
                isAuthenticated &&
                circleId > 0 &&
                circleAuthorizationToken &&
                packClass
            ),
        };
    },
};

export function useMediaPack(packClass?: string) {
    const { activeCircleId: circleId } = useActiveCircle();
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const { circle } = useCircle(circleId);
    const circleAuthorizationToken = circle?.authorizationJWT ?? '';
    const { queryKey, queryFn, queryOptions } = mediaPackQueryOptions;
    const rHGApp = getRhgAppHeader();

    const { data, ...otherProps } = useQuery({
        queryKey: queryKey(packClass!, circleId),
        queryFn: async () =>
            queryFn(
                getAccessTokenSilently,
                packClass!,
                circleId,
                circleAuthorizationToken,
                rHGApp
            ),
        ...queryOptions(
            isAuthenticated,
            circleId,
            circleAuthorizationToken,
            packClass
        ),
    });
    return {
        packClass: data,
        ...otherProps,
    };
}
